import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { joiResolver } from "@hookform/resolvers/joi";
import {
  forgotPasswordSchema,
  shownFieldError,
} from "../../utils/validationPatterns";
import { Link, useNavigate } from "react-router-dom";
import useAuth from "../../hooks/useAuth";
import { Helmet } from "react-helmet-async";
import { useTranslation } from "react-i18next";
import SocialAuth from "../../components/pages/lmcUserVIew/SocialAuth";
import { IResolveParams } from "reactjs-social-login";
import { CustomButton } from "../../components/atoms/CustomButton";
import { useOwnderForgotPassword } from "../../hooks/entity";
import { useUserSocialForgotPassword } from "../../hooks/users";
import { Timer } from "../../utils/Timer";

const ForgotPassword = () => {
  const [t] = useTranslation();
  const navigate = useNavigate();
  const auth = useAuth();

  const from = "/control-panel";

  const [showTimer, setShowTimer] = useState(false);

  useEffect(() => {
    let cleaner = true;

    if (cleaner && auth.user.isAuthenticated) navigate(from);

    return () => {
      cleaner = false;
    };
  }, [auth, from, navigate]);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm({
    resolver: joiResolver(forgotPasswordSchema),
  });

  const { mutate: forgetPassword, isLoading: isForgetting } =
    useOwnderForgotPassword();
  const { mutate: socialForgetPassword, isLoading: isForgettingSocial } =
    useUserSocialForgotPassword();

  const onSubmit = (data: any) => {
    forgetPassword(data.user_email, {
      onSuccess: (res) => {
        setShowTimer(true);
      },
      onError: () => setShowTimer(false),
    });
  };

  const handleLoginStartSocialAuth = () => {};

  const handleResolveSocialAuth = (
    provider: IResolveParams["provider"],
    data: IResolveParams["data"]
  ) => {
    try {
      const cpPayload = {
        access_token:
          provider === "google"
            ? data?.access_token
            : data?.authorization?.id_token,
        provider,
      };

      socialForgetPassword(
        {
          data: { providerPayload: cpPayload },
        },
        {
          onSuccess({ data }) {
            onSubmit({
              user_email: data.element.user_email,
            });
          },
        }
      );
    } catch (ex) {
      console.log(ex);
    }
  };

  const handleRejectSocialAuth = (err: any) => {
    console.log(err);
  };

  return (
    <div className="auth-page min-vh-100 d-flex align-items-center">
      <Helmet>
        <title>Forgot password | Super User Manager</title>
        <meta
          name="description"
          content="Super User Manager - Forgot password"
        />
        <meta name="robots" content="noindex" />
        <link
          rel="canonical"
          href={`${process.env.REACT_APP_HOST_URL}/forgot-password`}
        />
      </Helmet>

      <div className="flex-col justify-start items-center gap-8 inline-flex">
        <SocialAuth
          className="w-full"
          providers={["Apple", "Google"]}
          onLoginStartSocialAuthStart={handleLoginStartSocialAuth}
          onResolveSocialAuth={handleResolveSocialAuth}
          onRejectSocialAuth={handleRejectSocialAuth}
          providersIcon={{
            Apple: (
              <div className="px-2.5 w-full h-11 p-2 bg-white hover:bg-blue-400/5 rounded-md shadow border border-gray-300 flex flex-row justify-center items-center cursor-pointer relative mb-4">
                <img
                  src="../../images/apple-black.svg"
                  alt=""
                  className="w-6 h-6 absolute top-[10px] left-[10px]"
                />
                <p className="text-gray-700 text-sm font-medium font-figtree leading-tight">
                  {t("forgotPassword.socialApple")}
                </p>
              </div>
            ),
            Google: (
              <div className="px-2.5 w-full h-11 p-2 bg-white hover:bg-blue-400/5  rounded-md shadow border border-gray-300 flex flex-row justify-center items-center cursor-pointer relative">
                <img
                  src="../../images/google.svg"
                  alt=""
                  className="w-6 h-6 absolute top-[10px] left-[10px]"
                />
                <p className="text-gray-700 text-sm font-medium font-figtree leading-tight">
                  {t("forgotPassword.socialGoogle")}
                </p>
              </div>
            ),
          }}
          redirectUrl={{
            Apple:
              (process.env.REACT_APP_HOST_URL as string) + "/forgot-password",
            Google:
              (process.env.REACT_APP_HOST_URL as string) + "/forgot-password",
          }}
        />

        <form onSubmit={handleSubmit(onSubmit)}>
          <div className="self-stretch flex-col justify-start items-center gap-6 flex mb-8">
            <div className="flex-col justify-start items-center gap-2 flex">
              <div className="text-center text-gray-700 text-2xl font-bold font-figtree leading-9">
                {t("forgotPassword.title")}
              </div>
              <div className="text-gray-700 text-sm text-center font-normal font-figtree leading-tight">
                {t("forgotPassword.loginNote")}{" "}
              </div>
            </div>
          </div>

          <div className="self-stretch flex-col justify-start items-start gap-4 flex">
            <div
              className={`self-stretch bg-white rounded-md border ${
                Object.keys(errors).length > 0
                  ? "border-red-500"
                  : "border-gray-300"
              } flex-col justify-start items-start inline-flex`}
            >
              <div className="self-stretch px-3 py-2 bg-white rounded-md shadow justify-start items-center inline-flex">
                <input
                  type="email"
                  placeholder={t("forgotPassword.label1").toString()}
                  className="text-gray-700 focus:ring-0 p-0 border-none text-base font-normal font-figtree leading-normal w-full"
                  {...register("user_email")}
                />
              </div>
            </div>

            {Object.keys(errors).length > 0 && (
              <div>
                {Object.keys(errors).map((key) => (
                  <p key={key} className="mb-0 text-xs text-red-600">
                    {shownFieldError(t, errors[key]?.message?.toString())}
                  </p>
                ))}
              </div>
            )}

            <CustomButton
              type="submit"
              className="w-full"
              variant="primary"
              disabled={isForgetting || showTimer || isForgettingSocial}
              isLoading={isForgetting || isForgettingSocial}
              comp={
                showTimer && <Timer setShowTimer={setShowTimer} ttl={120} />
              }
              title={t("forgotPassword.resetBtn") as string}
            />
          </div>
        </form>

        <div className="justify-start items-center gap-1 inline-flex">
          <div className="text-gray-500 text-sm font-normal font-figtree leading-tight">
            {t("resetPassword.loginNote")}
          </div>
          <div className="justify-start items-center flex">
            <Link
              to={"/login"}
              className="text-blue-600 text-sm font-medium font-figtree leading-tight"
            >
              {t("resetPassword.loginBtn")}
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ForgotPassword;
